import { useState, useCallback, useRef } from 'react';
import L from 'leaflet';

/**
 * Starts a one-off polygon draw and invokes onComplete(paths) when done.
 * Also supports editing existing polygons with save/cancel/undo.
 */
export default function useJobsiteDrawing(mapRef) {
  const handlerRef = useRef(null);
  const tempLayerRef = useRef(null);
  const originalPathsRef = useRef(null);
  const onCompleteRef = useRef(null);
  const onCancelRef = useRef(null);
  const drawMapRef = useRef(null);
  const drawCreatedHandlerRef = useRef(null);
  const drawVertexHandlerRef = useRef(null);
  const drawStopHandlerRef = useRef(null);
  const onDrawCancelRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [drawVertexCount, setDrawVertexCount] = useState(0);

  const clearDrawListeners = useCallback(() => {
    const map = drawMapRef.current;
    if (map && drawCreatedHandlerRef.current) {
      map.off(L.Draw.Event.CREATED, drawCreatedHandlerRef.current);
    }
    if (map && drawVertexHandlerRef.current) {
      map.off(L.Draw.Event.DRAWVERTEX, drawVertexHandlerRef.current);
    }
    if (map && drawStopHandlerRef.current) {
      map.off(L.Draw.Event.DRAWSTOP, drawStopHandlerRef.current);
    }
    drawMapRef.current = null;
    drawCreatedHandlerRef.current = null;
    drawVertexHandlerRef.current = null;
    drawStopHandlerRef.current = null;
  }, []);

  const startPolygonDraw = useCallback((onComplete, onCancel) => {
    const map = mapRef.current;
    if (!map) return;

    // Cancel any previous draw/edit
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
    clearDrawListeners();
    setEditing(false);
    setDrawing(true);
    setDrawVertexCount(0);
    onDrawCancelRef.current = onCancel || null;

    const handler = new L.Draw.Polygon(map, {
      shapeOptions: { color: '#2e7d32', weight: 2, dashArray: '4, 4' },
      allowIntersection: false,
      showArea: false,
    });

    const onCreated = (e) => {
      clearDrawListeners();
      setDrawing(false);
      setDrawVertexCount(0);
      onDrawCancelRef.current = null;
      const layer = e.layer;
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0] || [];
        const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        try { map.removeLayer(layer); } catch (_) {}
        if (paths.length >= 3 && onComplete) onComplete(paths);
      }
      handlerRef.current = null;
    };

    const onDrawVertex = () => {
      setDrawVertexCount(handler._markers?.length || 0);
    };

    const onDrawStop = () => {
      if (handlerRef.current !== handler) return;
      clearDrawListeners();
      handlerRef.current = null;
      setDrawing(false);
      setDrawVertexCount(0);
      const cancel = onDrawCancelRef.current;
      onDrawCancelRef.current = null;
      cancel?.();
    };

    drawMapRef.current = map;
    drawCreatedHandlerRef.current = onCreated;
    drawVertexHandlerRef.current = onDrawVertex;
    drawStopHandlerRef.current = onDrawStop;
    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.DRAWVERTEX, onDrawVertex);
    map.on(L.Draw.Event.DRAWSTOP, onDrawStop);
    handler.enable();
    handlerRef.current = handler;
  }, [mapRef, clearDrawListeners]);

  /**
   * Edit an existing polygon — renders it as editable on the map.
   * User must use confirmEdit / cancelEdit / undoEdit to finish.
   */
  const startPolygonEdit = useCallback((existingPaths, onComplete, onCancel) => {
    const map = mapRef.current;
    if (!map || !existingPaths?.length) return;

    // Cancel any in-progress draw/edit
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
    clearDrawListeners();
    setDrawing(false);
    setDrawVertexCount(0);
    setEditing(true);
    originalPathsRef.current = existingPaths;
    onCompleteRef.current = onComplete;
    onCancelRef.current = onCancel;

    const latlngs = existingPaths.map(p => [p.lat, p.lng]);
    const editLayer = L.polygon(latlngs, {
      color: '#ff9800',
      weight: 3,
      opacity: 0.9,
      fillColor: '#ff9800',
      fillOpacity: 0.1,
      dashArray: '6, 4',
    });
    editLayer.addTo(map);
    editLayer.editing.enable();
    tempLayerRef.current = editLayer;

    handlerRef.current = {
      disable: () => {
        try { editLayer.editing.disable(); } catch (_) {}
        try { map.removeLayer(editLayer); } catch (_) {}
        tempLayerRef.current = null;
      },
    };
  }, [mapRef, clearDrawListeners]);

  const confirmEdit = useCallback(() => {
    const editLayer = tempLayerRef.current;
    if (!editLayer) return;
    const updatedLatLngs = editLayer.getLatLngs()[0] || [];
    const paths = updatedLatLngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
    if (handlerRef.current?.disable) handlerRef.current.disable();
    handlerRef.current = null;
    setEditing(false);
    if (paths.length >= 3 && onCompleteRef.current) onCompleteRef.current(paths);
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
  }, []);

  const cancelEdit = useCallback(() => {
    if (handlerRef.current?.disable) handlerRef.current.disable();
    handlerRef.current = null;
    setEditing(false);
    if (onCancelRef.current) onCancelRef.current();
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
  }, []);

  const undoEdit = useCallback(() => {
    const map = mapRef.current;
    const original = originalPathsRef.current;
    if (!map || !original?.length || !tempLayerRef.current) return;

    // Remove current edit layer, recreate from original
    if (handlerRef.current?.disable) handlerRef.current.disable();

    const latlngs = original.map(p => [p.lat, p.lng]);
    const editLayer = L.polygon(latlngs, {
      color: '#ff9800',
      weight: 3,
      opacity: 0.9,
      fillColor: '#ff9800',
      fillOpacity: 0.1,
      dashArray: '6, 4',
    });
    editLayer.addTo(map);
    editLayer.editing.enable();
    tempLayerRef.current = editLayer;

    handlerRef.current = {
      disable: () => {
        try { editLayer.editing.disable(); } catch (_) {}
        try { map.removeLayer(editLayer); } catch (_) {}
        tempLayerRef.current = null;
      },
    };
  }, [mapRef]);

  const cancelDraw = useCallback(() => {
    const cancel = onDrawCancelRef.current;
    onDrawCancelRef.current = null;
    clearDrawListeners();
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
    setDrawing(false);
    setDrawVertexCount(0);
    setEditing(false);
    onCompleteRef.current = null;
    onCancelRef.current = null;
    originalPathsRef.current = null;
    cancel?.();
  }, [clearDrawListeners]);

  const undoDrawPoint = useCallback(() => {
    const handler = handlerRef.current;
    if (!drawing || typeof handler?.deleteLastVertex !== 'function') return;
    handler.deleteLastVertex();
    setDrawVertexCount(handler._markers?.length || Math.max(0, drawVertexCount - 1));
  }, [drawing, drawVertexCount]);

  return {
    startPolygonDraw,
    startPolygonEdit,
    confirmEdit,
    cancelEdit,
    undoEdit,
    undoDrawPoint,
    cancelDraw,
    editing,
    drawing,
    drawVertexCount,
  };
}
